"use client";

import { useCallback, useMemo, useState } from "react";
import type {
  CircleLayerSpecification,
  ExpressionSpecification,
  GeoJSONSource,
  Map as MapLibreMap,
  MapLayerMouseEvent,
} from "maplibre-gl";

import {
  ProfileMapLibreSurface,
  type ProfileMapCenter,
  type ProfileMapLabels,
  type ProfileMapLibreModule,
} from "./ProfileMapLibreSurface";
import {
  buildMissionHeatmapTooltipContent,
  type MissionHeatmapLocationInput,
} from "./missionHeatmapDisplay";
import { ProfileMissionExperienceTooltip } from "./ProfileMissionExperienceTooltip";
import {
  boundsFromMissionHeatmapLocations,
  buildMissionHeatmapPointFeatureCollection,
  type MissionHeatmapPointFeatureCollection,
} from "./profileMissionHeatmapMapGeoJson";

const missionExperienceSourceId = "profile-mission-experience-points";
const missionExperienceLayerId = "profile-mission-experience-markets";

export type ProfileMissionHeatmapMapLabels = ProfileMapLabels & Readonly<{
  tooltipMissionCount: (count: number) => string;
  tooltipPortfolioPercentage: (percentage: number) => string;
  tooltipOperatingAreasSuffix: string;
}>;

export type ProfileMissionHeatmapMapProps = Readonly<{
  center: ProfileMapCenter;
  className?: string;
  componentInstance?: string;
  labels: ProfileMissionHeatmapMapLabels;
  locations: readonly MissionHeatmapLocationInput[];
  totalMissions?: number;
  mapEnabled?: boolean;
  maplibreLoader?: () => Promise<ProfileMapLibreModule>;
  styleUrl?: string;
}>;

type ExperienceMapTooltipState = Readonly<{
  x: number;
  y: number;
  location: MissionHeatmapLocationInput;
}>;

const metersPerPixelAtZoomZeroEquator = 156543.03392;
const milesToMeters = 1609.34;
const experienceCircleRadiusMaxZoom = 16;

function metersPerPixelAtZoomExpression(zoom: number): ExpressionSpecification {
  return [
    "/",
    [
      "*",
      metersPerPixelAtZoomZeroEquator,
      ["cos", ["*", ["get", "latitude"], 0.017453292519943295]],
    ],
    Math.pow(2, zoom),
  ];
}

function experienceCircleRadiusPixelsAtZoom(zoom: number): ExpressionSpecification {
  return [
    "/",
    ["*", ["get", "radiusMiles"], milesToMeters],
    metersPerPixelAtZoomExpression(zoom),
  ];
}

function experienceCircleRadiusExpression(): ExpressionSpecification {
  // MapLibre only allows ["zoom"] inside a top-level interpolate/step expression.
  const expression: ExpressionSpecification = ["interpolate", ["linear"], ["zoom"]];
  for (let zoom = 0; zoom <= experienceCircleRadiusMaxZoom; zoom += 1) {
    expression.push(zoom, experienceCircleRadiusPixelsAtZoom(zoom));
  }
  return expression;
}

function experienceCircleColorExpression(): ExpressionSpecification {
  return [
    "match",
    ["get", "heatLevel"],
    "top_10",
    "#dc2626",
    "top_25",
    "#f97316",
    "top_50",
    "#22c55e",
    "bottom_50",
    "#3b82f6",
    "rgba(0, 0, 0, 0)",
  ];
}

function experienceCircleLayerPaint(): NonNullable<CircleLayerSpecification["paint"]> {
  return {
    "circle-blur": 0.15,
    "circle-color": experienceCircleColorExpression(),
    "circle-opacity": [
      "*",
      ["get", "heatOpacity"],
      0.72,
    ],
    "circle-radius": experienceCircleRadiusExpression(),
    "circle-stroke-color": "#ffffff",
    "circle-stroke-opacity": 0.35,
    "circle-stroke-width": 1,
  };
}

function clearExperienceMapLayers(map: MapLibreMap): void {
  if (map.getLayer(missionExperienceLayerId)) {
    map.removeLayer(missionExperienceLayerId);
  }
  if (map.getSource(missionExperienceSourceId)) {
    map.removeSource(missionExperienceSourceId);
  }
}

function updateExperienceMapLayers(
  map: MapLibreMap,
  collection: MissionHeatmapPointFeatureCollection,
): void {
  if (collection.features.length === 0) {
    clearExperienceMapLayers(map);
    return;
  }

  const source = map.getSource(missionExperienceSourceId) as GeoJSONSource | undefined;
  if (source) {
    source.setData(collection as never);
    if (!map.getLayer(missionExperienceLayerId)) {
      map.addLayer({
        id: missionExperienceLayerId,
        type: "circle",
        source: missionExperienceSourceId,
        paint: experienceCircleLayerPaint(),
      });
    }
    return;
  }

  map.addSource(missionExperienceSourceId, {
    type: "geojson",
    data: collection as never,
  });
  map.addLayer({
    id: missionExperienceLayerId,
    type: "circle",
    source: missionExperienceSourceId,
    paint: experienceCircleLayerPaint(),
  });
}

export function ProfileMissionHeatmapMap({
  center,
  className,
  componentInstance = "profileMissionHeatmapMap",
  labels,
  locations,
  totalMissions,
  mapEnabled,
  maplibreLoader,
  styleUrl,
}: ProfileMissionHeatmapMapProps) {
  const [tooltip, setTooltip] = useState<ExperienceMapTooltipState | null>(null);
  const pointCollection = useMemo(
    () => buildMissionHeatmapPointFeatureCollection(locations),
    [locations],
  );
  const fitBounds = useMemo(
    () => boundsFromMissionHeatmapLocations(locations),
    [locations],
  );
  const updateLayers = useCallback(
    (map: MapLibreMap) => {
      updateExperienceMapLayers(map, pointCollection);
    },
    [pointCollection],
  );
  const bindMap = useCallback((map: MapLibreMap) => {
    if (locations.length === 0) {
      setTooltip(null);
      return undefined;
    }

    const handleMouseMove = (event: MapLayerMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature || typeof feature.properties?.locationKey !== "string") {
        setTooltip(null);
        map.getCanvas().style.cursor = "";
        return;
      }

      const location = locations.find((entry) => entry.locationKey === feature.properties?.locationKey);
      if (!location) {
        setTooltip(null);
        map.getCanvas().style.cursor = "";
        return;
      }

      map.getCanvas().style.cursor = "pointer";
      setTooltip({
        x: event.point.x,
        y: event.point.y,
        location,
      });
    };

    const handleMouseLeave = () => {
      setTooltip(null);
      map.getCanvas().style.cursor = "";
    };

    map.on("mousemove", missionExperienceLayerId, handleMouseMove);
    map.on("mouseleave", missionExperienceLayerId, handleMouseLeave);

    return () => {
      map.off("mousemove", missionExperienceLayerId, handleMouseMove);
      map.off("mouseleave", missionExperienceLayerId, handleMouseLeave);
      map.getCanvas().style.cursor = "";
      setTooltip(null);
    };
  }, [locations]);

  const tooltipContent = tooltip
    ? buildMissionHeatmapTooltipContent(tooltip.location, {
        missionCount: labels.tooltipMissionCount,
        portfolioPercentage: labels.tooltipPortfolioPercentage,
        operatingAreasSuffix: labels.tooltipOperatingAreasSuffix,
      }, totalMissions)
    : null;

  return (
    <ProfileMapLibreSurface
      bindMap={bindMap}
      center={center}
      className={["profile-mission-heatmap-map", className].filter(Boolean).join(" ")}
      componentInstance={componentInstance}
      fitBounds={fitBounds}
      interactive={locations.length > 0}
      labels={labels}
      mapEnabled={mapEnabled}
      maplibreLoader={maplibreLoader}
      renderOverlay={() => (
        tooltip && tooltipContent ? (
          <div className="profile-mission-experience-tooltip-layer">
            <ProfileMissionExperienceTooltip
              anchorX={tooltip.x}
              anchorY={tooltip.y}
              content={tooltipContent}
            />
          </div>
        ) : null
      )}
      styleUrl={styleUrl}
      updateLayers={updateLayers}
    />
  );
}
